import React, { useState, useEffect, useCallback } from 'react';
import { Box, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { AdminMenuBar } from '../../utils/admin-menu-bar';
import { AdminCfWorkers as AdminCfWorkersApi } from '../../../api/admin-cf-usage';

const CELL = { whiteSpace: 'nowrap', py: 0.5, px: 1.5 };
const HEAD_CELL = { ...CELL, fontWeight: 600 };

const REQ_PRICE = 0.3 / 1_000_000;
const CPU_MS_PRICE = 0.02 / 1_000_000;

function CfWorkers() {
    const [state, setState] = useState({ loading: false, data: { workers: [], pages: [] }, error: null });

    const refresh = useCallback(() => AdminCfWorkersApi(setState), []);
    useEffect(() => {
        refresh();
    }, [refresh]);

    const rows = [...(state.data?.workers ?? []), ...(state.data?.pages ?? [])];

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <AdminMenuBar apiItems={[{ label: 'Refresh', onClick: refresh }]} />
            <Box sx={{ overflow: 'auto', flex: 1 }}>
                {state.error && (
                    <Typography variant='caption' color='error' sx={{ display: 'block', mb: 1 }}>
                        {state.error}
                    </Typography>
                )}
                {rows.length === 0 && !state.loading && !state.error ? (
                    <Typography variant='body2' color='text.secondary'>
                        No worker activity.
                    </Typography>
                ) : (
                    <Table size='small' sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={HEAD_CELL}>Script</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Requests</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Errors</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>CPU Time</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Est. Cost (month)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.loading
                                ? Array.from({ length: 4 }, (_, i) => (
                                      <TableRow key={i}>
                                          {[0, 1, 2, 3, 4].map((j) => (
                                              <TableCell key={j} sx={CELL}>
                                                  <Skeleton />
                                              </TableCell>
                                          ))}
                                      </TableRow>
                                  ))
                                : rows.map((r) => {
                                      const cost = r.requests * REQ_PRICE + (r.cpu_ms ?? 0) * CPU_MS_PRICE;
                                      return (
                                          <TableRow key={r.script} hover>
                                              <TableCell sx={CELL}>{r.script}</TableCell>
                                              <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                                  {r.requests.toLocaleString()}
                                              </TableCell>
                                              <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                                  {r.errors != null ? r.errors.toLocaleString() : '—'}
                                              </TableCell>
                                              <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                                  {r.cpu_ms != null ? `${r.cpu_ms.toLocaleString()} ms` : '—'}
                                              </TableCell>
                                              <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                                  ${cost.toFixed(4)}
                                              </TableCell>
                                          </TableRow>
                                      );
                                  })}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Stack>
    );
}

export { CfWorkers };
